import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Input,
  Menu,
  MenuItem,
  Text,
  Tooltip,
} from "@showitinc/showit-wc";
import When from "../../../components/utils/When";
import PropertyEditor from "../../../components/PropertyEditor";
import {
  CUSTOM_CODE_LABEL,
  CUSTOM_CODE_HEAD_HTML,
  CUSTOM_CODE_CSS,
  CUSTOM_CODE_TOP_BODY_HTML,
  CUSTOM_CODE_BOTTOM_BODY_HTML,
  INCLUDE_WITH_EXPORT,
  APPLY_TO,
} from "../../../constants/DesignConstants.mjs";
import useStoreState from "../../../components/providers/useStoreState";
import SiteDesignStore from "../../../stores/SiteDesignStore";
import styles from "./SiteSettingsModal.module.css";
import HelpConstants from "../../../constants/HelpConstants";

const CustomCodePanel = ({ selectedGlobalCodeData, onPropChange, onDeleteCustomCode }) => {
  const canvasSets = useStoreState(SiteDesignStore, SiteDesignStore.getCanvasSetList);

  const [hasHeadHTML, setHasHeadHTML] = useState(selectedGlobalCodeData.get(CUSTOM_CODE_HEAD_HTML));
  const [hasCSS, setHasCSS] = useState(selectedGlobalCodeData.get(CUSTOM_CODE_CSS));
  const [isRenaming, setIsRenaming] = useState(false);
  const [newLabel, setNewLabel] = useState(selectedGlobalCodeData.get(CUSTOM_CODE_LABEL));
  const [hasTopBodyHTML, setHasTopBodyHTML] = useState(
    selectedGlobalCodeData.get(CUSTOM_CODE_TOP_BODY_HTML)
  );
  const [hasBottomBodyHTML, setHasBottomBodyHTML] = useState(
    selectedGlobalCodeData.get(CUSTOM_CODE_BOTTOM_BODY_HTML)
  );

  const labelInputRef = useRef(null);

  useEffect(() => {
    setHasHeadHTML(selectedGlobalCodeData.get(CUSTOM_CODE_HEAD_HTML));
    setHasCSS(selectedGlobalCodeData.get(CUSTOM_CODE_CSS));
    setHasTopBodyHTML(selectedGlobalCodeData.get(CUSTOM_CODE_TOP_BODY_HTML));
    setHasBottomBodyHTML(selectedGlobalCodeData.get(CUSTOM_CODE_BOTTOM_BODY_HTML));
    setNewLabel(selectedGlobalCodeData.get(CUSTOM_CODE_LABEL));
  }, [selectedGlobalCodeData]);

  const _onSetLabel = () => {
    onPropChange({ name: CUSTOM_CODE_LABEL, value: newLabel });
    labelInputRef.current.blur();
    setIsRenaming(false);
  };

  const onResetLabel = () => {
    setNewLabel(selectedGlobalCodeData.get(CUSTOM_CODE_LABEL));
    setIsRenaming(false);
  };

  return (
    <div className={styles.modalContent}>
      <div className={styles.centerSection}>
        <div className={styles.headerSection}>
          <Box d="flex" py="3" pl="3">
            <When it={!isRenaming}>
              <Text variant="heading-1" className={styles.codeTitle}>
                {selectedGlobalCodeData.get(CUSTOM_CODE_LABEL)}
              </Text>
            </When>
            <When it={isRenaming}>
              <Input
                ref={labelInputRef}
                value={newLabel}
                fullWidth
                className={`${styles.labelEditor} ${isRenaming ? styles.noTopPadding : ""}`}
                onSISubmit={_onSetLabel}
                onSIInput={(e) => setNewLabel(e.target.value)}
              />
            </When>
            <Button
              iconPrefix={isRenaming ? "check" : "edit"}
              variant="minimal"
              title={isRenaming ? "Save" : "Rename"}
              onClick={() => {
                if (isRenaming) {
                  _onSetLabel();
                } else {
                  setIsRenaming(true);
                  setTimeout(() => labelInputRef.current.focus(), 0);
                }
              }}
            >
              {isRenaming ? "save" : ""}
            </Button>
            <When it={isRenaming}>
              <Button title="Cancel" variant="minimal" iconPrefix="close" onClick={onResetLabel}>
                Cancel
              </Button>
            </When>

            <When it={!isRenaming}>
              <Button
                className={styles.deleteButton}
                iconPrefix="trash"
                variant="minimal"
                onClick={onDeleteCustomCode}
              >
                Delete
              </Button>
            </When>
          </Box>

          <Box px="3" pb="3">
            <Text>
              Some third-party services require inserting code into specific HTML sections instead
              of directly embedding widgets or forms. Examples include online chat systems and
              tracking services.
            </Text>
          </Box>
        </div>

        <Box py="3">
          <When it={hasHeadHTML}>
            <PropertyEditor
              propName={CUSTOM_CODE_HEAD_HTML}
              value={selectedGlobalCodeData.get(CUSTOM_CODE_HEAD_HTML)}
              label="Head HTML (inside <head>)"
              onChange={onPropChange}
              language="html"
            />
          </When>

          <When it={hasCSS}>
            <PropertyEditor
              propName={CUSTOM_CODE_CSS}
              value={selectedGlobalCodeData.get(CUSTOM_CODE_CSS)}
              label="CSS (inside <style>)"
              onChange={onPropChange}
              language="css"
            />
          </When>

          <When it={hasTopBodyHTML}>
            <PropertyEditor
              propName={CUSTOM_CODE_TOP_BODY_HTML}
              value={selectedGlobalCodeData.get(CUSTOM_CODE_TOP_BODY_HTML)}
              label="Start of page HTML (after <body>)"
              onChange={onPropChange}
              language="html"
            />
          </When>

          <When it={hasBottomBodyHTML}>
            <PropertyEditor
              propName={CUSTOM_CODE_BOTTOM_BODY_HTML}
              value={selectedGlobalCodeData.get(CUSTOM_CODE_BOTTOM_BODY_HTML)}
              label="End of page HTML (before </body>)"
              onChange={onPropChange}
              language="html"
            />
          </When>
        </Box>

        <Box p="3">
          <When it={!hasBottomBodyHTML || !hasCSS || !hasTopBodyHTML || !hasHeadHTML}>
            <Menu flip="true">
              <Button
                variant="outline"
                iconPrefix="add"
                slot="trigger-element"
                popovertarget="menu"
              >
                Add code to...
              </Button>

              <When it={!hasHeadHTML}>
                <MenuItem onClick={() => setHasHeadHTML(true)}>Add HTML to the head</MenuItem>
              </When>

              <When it={!hasCSS}>
                <MenuItem onClick={() => setHasCSS(true)}>Add CSS to the head</MenuItem>
              </When>

              <When it={!hasTopBodyHTML}>
                <MenuItem onClick={() => setHasTopBodyHTML(true)}>
                  Add HTML to the start of the page
                </MenuItem>
              </When>

              <When it={!hasBottomBodyHTML}>
                <MenuItem onClick={() => setHasBottomBodyHTML(true)}>
                  Add HTML to the end of the page
                </MenuItem>
              </When>
            </Menu>
          </When>
        </Box>
      </div>

      <div className={styles.propertiesPanel}>
        <Box p="3">
          <Text variant="header-2">Site Canvas Sets</Text>
        </Box>
        {canvasSets?.size > 0 ? (
          canvasSets.map((canvasSet) => (
            <Checkbox
              key={canvasSet.get("id")}
              checked={selectedGlobalCodeData?.get(APPLY_TO)?.includes(canvasSet.get("id"))}
              onSIChange={(e) => {
                const canvasId = canvasSet.get("id");
                const currentApplyTo = selectedGlobalCodeData.get(APPLY_TO) || [];
                const newApplyTo = e.target.checked
                  ? [...currentApplyTo, canvasId]
                  : currentApplyTo.filter((id) => id !== canvasId);
                onPropChange({ name: APPLY_TO, value: newApplyTo });
              }}
              label={canvasSet.get("name", "")}
            />
          ))
        ) : (
          <Alert
            variant="info"
            stacked
            weight="bold"
            message="No canvas sets available. Create a canvas set to apply the global code to your specified pages."
            ctaText="Learn More"
            ctaLink={HelpConstants.SITE_CANVAS_SET}
          />
        )}
        <hr className={styles.hr} />
        <Tooltip>
          <span slot="content">
            <Text>
              Select this if you want this code to be included when sharing your design key.
            </Text>
          </span>
          <Checkbox
            checked={selectedGlobalCodeData?.get(INCLUDE_WITH_EXPORT)}
            onSIChange={(e) => {
              onPropChange({ name: INCLUDE_WITH_EXPORT, value: e.target.checked });
            }}
            label="Include with design export"
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default CustomCodePanel;
